import PropTypes from 'prop-types';

import DonationCount from 'components/donation-counts/DonationCount';
import Layout from 'components/layout';
import Bubble from 'components/bubble';
import Heading from 'components/typography/heading';
import Text from 'components/typography/text';
import Anchor from 'components/typography/anchor';
import Tabs from 'components/tabs';
import Petition from 'components/petition/Petition';
import ResistCTAButtons from 'components/buttons/cta';
import userShareImg from 'images/shares/petition-share.png';
import {
  getTrendingPetitions,
  getPetitionsForUserCode,
} from 'lib/api/petitions';
import {
  PETITION_SIGNING_STATES,
  trimPetitions,
  fieldsListView,
} from 'lib/petitions';
import { PetitionTypeList } from 'utilities/propTypes';

import { stats, profile, empty, about } from './go.module.scss';
import { cta, ctaHeading } from '../common/petitions.module.scss';

const BASE_PATH = 'go';

export const getServerSideProps = async ({ params }) => {
  // For consistency reasons we jsut do this here.  Display of usernames is always lowercase (except the keyword bubbles)
  // and the cassini endpoint will normalize everything uppercase.
  const usercode = params.usercode.toLowerCase();
  const user = await getPetitionsForUserCode({ usercode });

  if (user.notFound) {
    return {
      notFound: true,
    };
  }

  const trendingPetitions = await getTrendingPetitions();

  const open = [];
  const closed = [];
  let signCount = 0;

  // Process each to add trending data and split into open/closed
  user.petitions.forEach((p) => {
    const sharecode = p.user_share_code;
    const isTrending = trendingPetitions.filter(
      (p) => p.petition_code === sharecode
    );

    // Move this to secondary level for if we ever use different time periods
    // This also happens in packages/site/src/lib/api/petitions/index.js
    p['trending'] = {
      sign_count_period: isTrending[0]?.trending?.sign_count_period || null,
    };

    // Add up all the total signatures they've gotten
    signCount = signCount + p.sign_count || 0;

    const is_closed = p.sign_status === PETITION_SIGNING_STATES.CLOSED;

    if (is_closed) {
      closed.push(p);
    } else {
      open.push(p);
    }
  });

  const slimOpen = trimPetitions({
    petitions: open,
    fieldsToKeep: fieldsListView,
  });
  const slimClosed = trimPetitions({
    petitions: closed,
    fieldsToKeep: fieldsListView,
  });

  return {
    props: {
      usercode,
      open: slimOpen,
      closed: slimClosed,
      signCount,
    },
  };
};

const User = ({ usercode, open, closed, signCount }) => {
  return (
    <Layout
      title={`@${usercode} on Resistbot`}
      description={`Join the campaigns @${usercode} is running on Resistbot!`}
      image={userShareImg}
      imageAltText={`Text Follow ${usercode} to 50409`}
      canonicalPath={`/${BASE_PATH}/${usercode.toLowerCase()}`}
    >
      <div className={profile}>
        <div className={about}>
          <Heading as="h1" size="h2">
            @{usercode}
          </Heading>
          <Text>
            Here are the campaigns @{usercode} has organized, text{' '}
            <Bubble>Go {usercode}</Bubble> to see these on Resistbot, or{' '}
            <Bubble>Follow {usercode}</Bubble> to be alerted to new campaigns.
          </Text>
        </div>
        <div className={stats}>
          <DonationCount count={signCount} text="Total Signers" />
        </div>
      </div>

      <PetitionsGroup open={open} closed={closed} usercode={usercode} />
      <CTA sharecode={usercode} />

      <Text>
        Want your own Resistbot keyword and a page like this one?{' '}
        <Anchor href="/donate?monthly=true">
          Become a monthly&nbsp;donor!
        </Anchor>
      </Text>
    </Layout>
  );
};

const PetitionsGroup = ({ open, closed, usercode }) => {
  const openGroup = open.map((petition) => (
    <Petition key={petition.petition_code} petition={petition} />
  ));
  const closedGroup = closed.map((petition) => (
    <Petition key={petition.petition_code} petition={petition} />
  ));

  const hasOpen = open.length > 0;
  const hasClosed = closed.length > 0;
  const hasBoth = hasOpen && hasClosed;
  const hasNone = !hasOpen && !hasClosed;

  const openTabText = hasOpen ? `Open (${open.length})` : `Open`;
  const closedTabText = hasClosed ? `Closed (${closed.length})` : `Closed`;

  // None of each
  if (hasNone) {
    return (
      <div className={empty}>
        Follow @{usercode} to receive a text when they launch their first
        campaign, or see <Anchor href="/petitions">top campaigns</Anchor>{' '}
        organized by other&nbsp;members.
      </div>
    );
  }
  // Some of both
  if (hasBoth) {
    return (
      <Tabs
        descriptor="petition types"
        searchDisabled={true}
        sections={[
          {
            contents: openGroup,
            title: openTabText,
          },
          {
            contents: closedGroup,
            title: closedTabText,
          },
        ]}
      ></Tabs>
    );
  }
  // Only open
  if (hasOpen && !hasClosed) {
    return openGroup;
  }
  // Only closed
  if (!hasOpen && hasClosed) {
    return (
      <Tabs
        descriptor="petition types"
        searchDisabled={true}
        sections={[
          {
            contents: openGroup,
            title: openTabText,
          },
          {
            contents: closedGroup,
            title: closedTabText,
          },
        ]}
      ></Tabs>
    );
  }
};

const CTA = ({ sharecode, is_closed = false }) => {
  const params = !is_closed ? { keyword: `follow ${sharecode}` } : {};
  const heading = !is_closed ? (
    <Heading as="h2" className={ctaHeading} size="h2">
      Follow @{sharecode}
    </Heading>
  ) : null;

  return (
    <div className={cta}>
      {heading}
      <ResistCTAButtons {...params} />
    </div>
  );
};

CTA.propTypes = {
  sharecode: PropTypes.string.isRequired,
  is_closed: PropTypes.bool,
};

User.propTypes = {
  usercode: PropTypes.string.isRequired,
  open: PropTypes.arrayOf(PetitionTypeList).isRequired,
  closed: PropTypes.arrayOf(PetitionTypeList).isRequired,
  signCount: PropTypes.number.isRequired,
};

export default User;
